﻿// packages/shared/src/utils/index.ts
import { Booking, HeatmapData, TimeRange, PlayerSlot } from '../types';

/**
 * Convert booking time to heatmap coordinates with 5-minute granularity
 */
export function bookingToHeatmapCoords(
  startTime: Date,
  minHour: number = 0,
  maxHour: number = 24
): { dayIndex: number; timeIndex: number } | null {
  const dayIndex = (startTime.getDay() + 6) % 7; // Convert Sunday=0 to Monday=0
  const hour = startTime.getHours();
  const minute = startTime.getMinutes();

  if (hour < minHour || hour >= maxHour) {
    return null; // Outside display range
  }

  // 5-minute granularity: 12 slots per hour
  const timeIndex = (hour - minHour) * 12 + Math.floor(minute / 5);
  return { dayIndex, timeIndex };
}

/**
 * Get intensity class based on player count
 */
export function getIntensityClass(playerCount: number, maxPlayerCount: number): string {
  if (playerCount === 0 || maxPlayerCount === 0) return 'green-intensity-0';
  const percentage = playerCount / maxPlayerCount;
  if (percentage <= 0.001 && playerCount > 0) return 'green-intensity-1';
  if (percentage <= 0.20) return 'green-intensity-1';
  if (percentage <= 0.40) return 'green-intensity-2';
  if (percentage <= 0.60) return 'green-intensity-3';
  if (percentage <= 0.80) return 'green-intensity-4';
  return 'green-intensity-5';
}

/**
 * Create empty heatmap matrix with 5-minute granularity
 */
export function createEmptyHeatmap(days: number = 7, hoursPerDay: number = 24): number[][] {
  const slotsPerHour = 12; // 5-minute slots
  const totalSlots = hoursPerDay * slotsPerHour;
  return Array(days).fill(null).map(() => Array(totalSlots).fill(0));
}

/**
 * Format time for display (HH:MM)
 */
export function formatTimeSlot(hour: number, minute: number): string {
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

/**
 * Calculate end time from start time and duration
 */
export function calculateEndTime(startTime: Date, durationMinutes: number): Date {
  const endTime = new Date(startTime.getTime() + durationMinutes * 60000);
  return endTime;
}

/**
 * Format time range for display
 */
export function formatTimeRange(startTime: Date, endTime: Date): string {
  const startHours = startTime.getHours().toString().padStart(2, '0');
  const startMinutes = startTime.getMinutes().toString().padStart(2, '0');
  const endHours = endTime.getHours().toString().padStart(2, '0');
  const endMinutes = endTime.getMinutes().toString().padStart(2, '0');
  
  return `${startHours}:${startMinutes} - ${endHours}:${endMinutes}`;
}

/**
 * Get formatted date (e.g., "Mon, May 19")
 */
export function formatDate(date: Date): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
}

/**
 * Validate booking data
 */
export function validateBooking(booking: Partial<Booking>): string[] {
  const errors: string[] = [];

  if (!booking.startTime || !booking.endTime) {
    errors.push('Start and end times are required');
  } else if (booking.startTime >= booking.endTime) {
    errors.push('End time must be after start time');
  } else {
    const duration = booking.endTime.getTime() - booking.startTime.getTime();
    const durationMinutes = duration / (1000 * 60);
    
    if (durationMinutes < 30) {
      errors.push('Booking must be at least 30 minutes');
    }
    if (durationMinutes > 300) {
      errors.push('Booking cannot exceed 5 hours');
    }
  }

  if (!booking.level || !['beginner', 'intermediate', 'advanced', 'expert'].includes(booking.level)) {
    errors.push('Valid level is required');
  }

  if (!booking.courtId) {
    errors.push('Court ID is required');
  }

  return errors;
}

/**
 * Generate dummy heatmap data with 5-minute granularity for development
 */
export function generateDummyHeatmap(minHour: number = 0, maxHour: number = 24): { heatmapData: HeatmapData; maxPlayers: number } {
  const hoursPerDay = maxHour - minHour;
  const heatmapData = createEmptyHeatmap(7, hoursPerDay);
  let maxPlayers = 0;

  // Add some random data with 5-minute granularity
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < hoursPerDay; hour++) {
      for (let slot = 0; slot < 12; slot++) { // 12 slots per hour (5-min each)
        const timeSlotIndex = hour * 12 + slot;
        
        // More activity during peak hours (10-2, 5-8)
        const realHour = hour + minHour;
        let baseChance = 0.05;
        
        if ((realHour >= 10 && realHour <= 14) || (realHour >= 17 && realHour <= 20)) {
          baseChance = 0.15;
        }
        
        if (Math.random() < baseChance) {
          const players = Math.floor(Math.random() * 8) + 1;
          heatmapData[day][timeSlotIndex] = players;
          if (players > maxPlayers) {
            maxPlayers = players;
          }
        }
      }
    }
  }

  // Create clusters of activity
  for (let i = 0; i < 15; i++) {
    const centerDay = Math.floor(Math.random() * 7);
    const centerHour = Math.floor(Math.random() * hoursPerDay);
    const centerSlot = Math.floor(Math.random() * 12);
    const centerIndex = centerHour * 12 + centerSlot;
    
    // Create a peak of activity
    const peakPlayers = Math.floor(Math.random() * 10) + 5;
    heatmapData[centerDay][centerIndex] = peakPlayers;
    if (peakPlayers > maxPlayers) {
      maxPlayers = peakPlayers;
    }
    
    // Create surrounding activity (fading out)
    for (let dOffset = -1; dOffset <= 1; dOffset++) {
      const day = (centerDay + dOffset + 7) % 7;
      for (let hOffset = -2; hOffset <= 2; hOffset++) {
        const hour = centerHour + hOffset;
        if (hour < 0 || hour >= hoursPerDay) continue;
        
        for (let sOffset = -2; sOffset <= 2; sOffset++) {
          const slot = centerSlot + sOffset;
          if (slot < 0 || slot >= 12) continue;
          
          const index = hour * 12 + slot;
          const distance = Math.abs(dOffset) + Math.abs(hOffset) + Math.abs(sOffset/2);
          
          if (distance > 0) {
            const players = Math.max(1, Math.floor(peakPlayers / (1 + distance)));
            
            // Only set if higher than existing value
            if (players > (heatmapData[day][index] || 0)) {
              heatmapData[day][index] = players;
              if (players > maxPlayers) {
                maxPlayers = players;
              }
            }
          }
        }
      }
    }
  }

  return { heatmapData, maxPlayers };
}

/**
 * Convert string duration to minutes
 */
export function durationToMinutes(duration: string | number): number {
  if (typeof duration === 'number') return duration;
  if (duration === 'any') return 0;
  
  const parsed = parseInt(duration, 10);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Format duration in minutes to human readable string
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (remainingMinutes === 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Get week start date (Monday) for any given date
 */
export function getWeekStart(date: Date): Date {
  const newDate = new Date(date);
  const day = newDate.getDay();
  const diff = newDate.getDate() - day + (day === 0 ? -6 : 1);
  newDate.setDate(diff);
  return newDate;
}

/**
 * Check if two dates are the same week
 */
export function isSameWeek(date1: Date, date2: Date): boolean {
  const week1Start = getWeekStart(new Date(date1));
  const week2Start = getWeekStart(new Date(date2));
  return week1Start.getTime() === week2Start.getTime();
}

/**
 * Generate daily time slots with 5-minute granularity
 */
export function generateTimeSlots(minHour: number = 0, maxHour: number = 24): { hour: number, minute: number }[] {
  const slots = [];
  for (let hour = minHour; hour < maxHour; hour++) {
    for (let minute = 0; minute < 60; minute += 5) {
      slots.push({ hour, minute });
    }
  }
  return slots;
}

/**
 * Convert selection to time range
 */
export function selectionToTimeRange(selection: any, weekStartDate: Date): TimeRange | null {
  if (!selection) return null;

  const startDay = Math.min(selection.start.day, selection.end.day);
  const endDay = Math.max(selection.start.day, selection.end.day);
  
  const startHour = selection.start.hour;
  const startMinute = selection.start.minute;
  const endHour = selection.end.hour;
  const endMinute = selection.end.minute;
  
  const startDate = new Date(weekStartDate);
  startDate.setDate(startDate.getDate() + startDay);
  startDate.setHours(startHour, startMinute, 0, 0);
  
  const endDate = new Date(weekStartDate);
  endDate.setDate(endDate.getDate() + endDay);
  endDate.setHours(endHour, endMinute, 0, 0);

  return { startDate, endDate };
}

/**
 * Filter player slots by time range
 */
export function filterPlayerSlots(slots: PlayerSlot[], timeRange: TimeRange): PlayerSlot[] {
  if (!timeRange) return slots;
  
  return slots.filter(slot => {
    const slotStart = slot.startTime;
    const slotEnd = calculateEndTime(slot.startTime, slot.duration);
    
    return (slotStart <= timeRange.endDate && slotEnd >= timeRange.startDate);
  });
}

/**
 * Get timezone offset for display
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const now = new Date();
    const timeZoneOffset = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value;
    
    return timeZoneOffset || timezone;
  } catch (error) {
    return timezone;
  }
}